import React, { useCallback, useEffect, useRef, useState } from 'react';
import Button from '../Button';
import './ListingDetailImageGallery.css';

export interface ListingDetailImageGalleryProps {
  images: string[];
  title?: string;
  className?: string;
}

/**
 * Bootstrap carousel + thumbnail strip with synced active state and smooth slide transitions.
 */
const ListingDetailImageGallery: React.FC<ListingDetailImageGalleryProps> = ({
  images,
  title = 'Ürün',
  className = '',
}) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const carouselRef = useRef<HTMLDivElement>(null);
  const carouselInstanceRef = useRef<{ to: (index: number) => void; dispose?: () => void } | null>(null);

  useEffect(() => {
    setActiveIndex(0);
  }, [images]);

  useEffect(() => {
    if (!carouselRef.current || images.length === 0) return undefined;

    const el = carouselRef.current;
    const bootstrap = (window as unknown as { bootstrap?: { Carousel: new (el: Element, opts?: object) => { to: (i: number) => void; dispose: () => void } } }).bootstrap;

    if (!bootstrap?.Carousel) return undefined;

    carouselInstanceRef.current?.dispose?.();
    const instance = new bootstrap.Carousel(el, { interval: false, ride: false });
    carouselInstanceRef.current = instance;

    const onSlid = (event: Event) => {
      const detail = event as Event & { to?: number };
      if (typeof detail.to === 'number') {
        setActiveIndex(detail.to);
      }
    };

    el.addEventListener('slid.bs.carousel', onSlid);

    return () => {
      el.removeEventListener('slid.bs.carousel', onSlid);
      instance.dispose();
      carouselInstanceRef.current = null;
    };
  }, [images]);

  const goToSlide = useCallback((index: number) => {
    if (index < 0 || index >= images.length) return;
    setActiveIndex(index);
    carouselInstanceRef.current?.to(index);
  }, [images.length]);

  if (images.length === 0) return null;

  return (
    <div className={className}>
      <div
        className="carousel slide mb-3"
        data-bs-ride="false"
        id="productCarousel"
        ref={carouselRef}
      >
        <div className="carousel-inner rounded">
          {images.map((image, index) => (
            <div key={`${image}-${index}`} className={`carousel-item${index === activeIndex ? ' active' : ''}`}>
              <img
                alt={`${title} ${index + 1}`}
                className="d-block w-100"
                src={image}
              />
            </div>
          ))}
        </div>
        {images.length > 1 && (
          <>
            <Button
              className="carousel-control-prev custom-carousel-control"
              type="button"
              data-bs-target="#productCarousel"
              data-bs-slide="prev"
            >
              <div className="carousel-control-icon">
                <i className="fas fa-chevron-left" aria-hidden="true" />
              </div>
            </Button>
            <Button
              className="carousel-control-next custom-carousel-control"
              type="button"
              data-bs-target="#productCarousel"
              data-bs-slide="next"
            >
              <div className="carousel-control-icon">
                <i className="fas fa-chevron-right" aria-hidden="true" />
              </div>
            </Button>
          </>
        )}
      </div>

      {images.length > 1 && (
        <div className="thumbnail-gallery-container">
          <div className="thumbnail-gallery d-flex gap-2 overflow-auto px-2 py-1">
            {images.map((image, index) => (
              <div
                key={`thumb-${image}-${index}`}
                className={`thumbnail${activeIndex === index ? ' active' : ''}`}
                onClick={() => goToSlide(index)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    goToSlide(index);
                  }
                }}
                role="button"
                tabIndex={0}
                aria-label={`Görsel ${index + 1}`}
                aria-current={activeIndex === index ? 'true' : undefined}
              >
                <img alt={`Thumbnail ${index + 1}`} className="rounded" src={image} />
                <div className="thumbnail-overlay" />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ListingDetailImageGallery;
